import { AbstractControl, Validators as BuiltinValidators, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ValidatorKeys as BuiltinValidatorKeys } from '@sdpp-web/lib/ui/form';

export const ValidatorKeys = {
  onlyAlphabets: 'onlyAlphabets',
  email: 'email',
  phoneNumber: 'phoneNumber',
  emailGroup: 'emailGroup',
  min: 'min',
  max: 'max',
  invalidEmailGroup: 'invalidEmailGroup'
};

export class Validators {
  /**
   * Conditionally validate the control value.
   *
   * @param predicate The predicate expression to check.
   * @param validatorFn The validator function to execute.
   *
   * @returns A validator function if predicate returns true, otherwise `null`.
   */
  public static conditional(predicate: () => boolean, validatorFn: ValidatorFn): ValidatorFn {
    return (control: AbstractControl) => (predicate() ? validatorFn(control) : null);
  }

  public static onlyAlphabets(control: AbstractControl): ValidationErrors | null {
    let result = BuiltinValidators.pattern(/^([ \u00c0-\u01ffa-zA-Z'-])+$/)(control);

    if (result != null) {
      result = {
        [ValidatorKeys.onlyAlphabets]: result[BuiltinValidatorKeys.PATTERN]
      };
    }

    return result;
  }

  public static email(control: AbstractControl): ValidationErrors | null {
    let result = BuiltinValidators.pattern(/^\s*([\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7})\s*/)(control);

    if (result) {
      result = { [ValidatorKeys.email]: result[BuiltinValidatorKeys.PATTERN] };
    }

    return result;
  }

  public static phoneNumber(control: AbstractControl): ValidationErrors | null {
    let result = BuiltinValidators.pattern(
      /^\+?(\d{1,3})?[-.\s]?\(?(\d{1,4})\)?[-.\s]?(\d{1,4})[-.\s]?(\d{1,4})[-.\s]?(\d{1,9})$/
    )(control);

    if (result != null) {
      result = { [ValidatorKeys.phoneNumber]: result[BuiltinValidatorKeys.PATTERN] };
    }

    return result;
  }

  public static emailGroup(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Return null if control.value is null or undefined
    }

    const emails = control.value.split(',').map((email: string) => email.trim());
    const emailPattern = /^\s*([\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7})\s*$/;

    for (const email of emails) {
      if (!emailPattern.test(email)) {
        return { [ValidatorKeys.invalidEmailGroup]: true };
      }
    }

    return null;
  }

  public static min(min: number): ValidatorFn {
    return control => {
      const error = BuiltinValidators.min(min)(control);

      return error != null
        ? {
            [ValidatorKeys.min]: error[BuiltinValidatorKeys.MIN]
          }
        : null;
    };
  }

  public static max(max: number): ValidatorFn {
    return control => {
      const error = BuiltinValidators.max(max)(control);

      return error != null
        ? {
            [ValidatorKeys.max]: error[BuiltinValidatorKeys.MAX]
          }
        : null;
    };
  }
}
